import api from './api';

/**
 * Trigger a browser file-save from a Blob.
 *
 * @param {Blob}   blob     — the content to save
 * @param {string} fileName — suggested filename shown in the browser save dialog
 */
export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Fetch a finalised repair bucket's PDF and save it to disk. The one function
 * every caller of `GET /repairs/buckets/:id/report.pdf` goes through — the
 * Repair PDF button (features/reports/hooks/useReportExports.js) and the
 * finalise-confirmation modal (components/reports/FinaliseRepairsModal.jsx,
 * immediately after a bucket is created) both call this instead of each
 * keeping their own copy of the blob/filename plumbing. Lives here (lib), not
 * in a features/ or components/ hook, so both layers can import it without
 * crossing the components → features boundary (see .eslintrc.cjs).
 *
 * @param {number} bucketId
 * @returns {Promise<string>} the filename saved
 */
export async function downloadRepairBucketReportPdf(bucketId) {
  const response = await api.get(`/repairs/buckets/${bucketId}/report.pdf`, { responseType: 'blob' });
  const contentDisposition = response.headers['content-disposition'];
  const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch?.[1] || 'Repair_Report.pdf';
  downloadBlob(response.data, filename);
  return filename;
}

/**
 * Recover the server's `{ error: '...' }` message from an Axios error, whether
 * the request used `responseType: 'blob'` or a plain JSON request. Axios never
 * JSON-parses a blob response body, so a 404/500/503/504 error from a blob
 * request arrives as `err.response.data` — a `Blob`, not a parsed object — and
 * reading `err.response?.data?.error` directly always yields `undefined` in
 * that case. Read the blob as text and parse it ourselves; for a normal JSON
 * error response, fall through to the usual `err.response?.data?.error` read.
 *
 * @param {*} err — the caught Axios error
 * @param {string} [fallback] — message to use when no server message is found
 */
export async function readBlobError(err, fallback = 'Failed to download report') {
  const blob = err?.response?.data;
  if (blob instanceof Blob) {
    try {
      const text = await blob.text();
      return JSON.parse(text)?.error || fallback;
    } catch {
      return fallback;
    }
  }
  return err?.response?.data?.error || err?.message || fallback;
}
